import { ArrowRight, ExternalLink, Handshake, Compass, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import Faq from "@/components/Faq";
import InquiryForm from "@/components/InquiryForm";
import { AFFILIATE_URL, IMAGES } from "@/lib/site";

const FAQS = [
  {
    q: "Do I need to sell anything?",
    a: "No. Affiliates make introductions; the evaluation, presentation, contracting, and implementation are handled by Aspire Partners and its provider network. You never carry a sales quota or manage a project.",
  },
  {
    q: "Who delivers the work when a business moves forward?",
    a: "Vetted specialist providers deliver every engagement. Riverstone and Aspire Partners help coordinate introductions and communication, but delivery belongs to the providers.",
  },
  {
    q: "Is this a job or employment with Riverstone?",
    a: "No. This is an independent referral opportunity, not salaried employment. Affiliates participate on their own schedule and remain independent professionals.",
  },
  {
    q: "Are earnings guaranteed?",
    a: "No. Compensation, if any, depends on the outcomes of introductions and the terms set by Aspire Partners. Nothing on this page should be read as a promise of income.",
  },
];

export default function BecomePartner() {
  return (
    <>
      <Seo
        title="Become a Partner — Aspire Partners | Riverstone Partners"
        description="Create new possibilities through the relationships you already have. Explore the Aspire Partners affiliate opportunity for consultants, executives, and advisors."
        path="/become-a-partner"
      />

      <section className="border-b border-charcoal/10 bg-sage/50 py-16 md:py-24">
        <div className="shell grid items-center gap-12 lg:grid-cols-[1.3fr_1fr]">
          <div>
            <p className="eyebrow text-forest">Become a Partner</p>
            <h1 className="mt-5 font-serif text-4xl font-light leading-[1.1] tracking-tight text-navy sm:text-5xl" data-testid="partner-title">
              Create new possibilities through the relationships you already have.
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-charcoal/80">
              Through the Aspire Partners opportunity, business professionals introduce
              the companies in their network to relevant solutions—without selling,
              implementing, or managing anything.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href={AFFILIATE_URL || "#partner-inquiry"}
                target={AFFILIATE_URL ? "_blank" : undefined}
                rel={AFFILIATE_URL ? "noopener noreferrer" : undefined}
                onClick={AFFILIATE_URL ? undefined : (e) => { e.preventDefault(); document.getElementById("partner-inquiry")?.scrollIntoView({ behavior: "smooth" }); }}
                className="btn-primary"
                data-testid="aspire-partner-apply-button"
              >
                Learn About Becoming a Partner
                {AFFILIATE_URL ? <ExternalLink className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}
              </a>
            </div>
          </div>
          <img
            src={IMAGES.groupLaptop}
            alt="Professionals collaborating around a laptop"
            width="1600"
            height="1100"
            loading="eager"
            className="aspect-[4/3] w-full object-cover shadow-soft"
          />
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="shell">
          <div className="grid gap-12 lg:grid-cols-3">
            {[
              {
                icon: Compass,
                t: "Who may be interested",
                b: "Consultants, executives, advisors, and fractional leaders—anyone whose professional network includes business owners facing cost, revenue, or operations challenges.",
              },
              {
                icon: Handshake,
                t: "How introductions work",
                b: "You identify a business in your network that may benefit, and make a simple introduction. Aspire Partners and its vendor network handle the evaluation, analysis, presentations, contracts, and implementation.",
              },
              {
                icon: ShieldCheck,
                t: "How you’re supported",
                b: "Aspire Partners provides the tools, training, and support behind the program, and the providers do the delivery work—so your credibility stays front and center.",
              },
            ].map((c, i) => (
              <Reveal key={c.t} delay={i * 0.08}>
                <div className="h-full border border-charcoal/10 bg-white p-8">
                  <c.icon className="h-7 w-7 text-forest" />
                  <h2 className="mt-5 font-serif text-2xl font-light text-navy">{c.t}</h2>
                  <p className="mt-3 leading-relaxed text-charcoal/75">{c.b}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1} className="mt-14">
            <div className="border-l-2 border-forest bg-sage/50 p-8 md:p-10">
              <h2 className="font-serif text-2xl text-navy">What the next step involves</h2>
              <p className="mt-4 max-w-3xl leading-relaxed text-charcoal/80">
                It starts with a short, no-pressure conversation with Riverstone Partners
                about your network and goals. If the opportunity looks mutual, we connect
                you with Aspire Partners to review the program in detail. Participation
                terms—including any membership requirements—are set by Aspire Partners and
                confirmed during that review.
              </p>
              <p className="mt-4 max-w-3xl text-sm leading-relaxed text-charcoal/60">
                This is an independent referral opportunity, not salaried employment, and
                no earnings are guaranteed.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-mist py-16 md:py-24">
        <div className="shell max-w-4xl">
          <Reveal>
            <p className="eyebrow text-forest">Common questions</p>
            <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-navy sm:text-4xl">Partner FAQs</h2>
          </Reveal>
          <Reveal delay={0.08} className="mt-8">
            <Faq items={FAQS} />
          </Reveal>
        </div>
      </section>

      <section id="partner-inquiry" className="py-16 md:py-24">
        <div className="shell grid gap-12 lg:grid-cols-[1fr_1.2fr] lg:gap-20">
          <Reveal>
            <p className="eyebrow text-river">Partner inquiry</p>
            <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-navy sm:text-4xl">
              Tell us about your network.
            </h2>
            <p className="mt-5 leading-relaxed text-charcoal/75">
              Share a little about who you work with and what you’d like to explore.
              Partner inquiries are reviewed separately from business-service inquiries,
              so your note reaches the right conversation.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <InquiryForm variant="partner" />
          </Reveal>
        </div>
      </section>
    </>
  );
}
